import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import CreatableSelect from "react-select/creatable";

const NicuOfficeExpenseModal = ({ isOpen, onClose, onSave, expenseToEdit }) => {
  const isEditMode = !!expenseToEdit;
  const [formData, setFormData] = useState({ name: null, amount: "", note: "" });
  const [itemOptions, setItemOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const BASE_URL = "https://api.muktihospital.com/api/nicu";

  // Fetch expense items for the dropdown
  useEffect(() => {
    if (isOpen) {
      (async () => {
        try {
          const res = await axios.get(`${BASE_URL}/office-expense-item`, { headers: { "x-api-key": API_KEY } });
          if (res.data.items) setItemOptions(res.data.items.map(i => ({ value: i.name, label: i.name })));
        } catch (error) {
          console.error("Error fetching options:", error);
        }
      })();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && expenseToEdit) {
        setFormData({
          name: { value: expenseToEdit.name, label: expenseToEdit.name },
          amount: expenseToEdit.amount,
          note: expenseToEdit.note || "",
        });
      } else {
        setFormData({ name: null, amount: "", note: "" });
      }
      setErrors({});
      setIsLoading(false);
    }
  }, [isOpen, expenseToEdit, isEditMode]);

  const handleCreateItem = async (inputValue) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/office-expense-item/add`, { name: inputValue }, { headers: { "x-api-key": API_KEY } });
      const newOption = { value: response.data.item.name, label: response.data.item.name };
      setItemOptions((prev) => [...prev, newOption]);
      setFormData((prev) => ({ ...prev, name: newOption }));
      toast.success(`Item "${inputValue}" created!`);
    } catch (error) {
      toast.error("Failed to create item.");
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Expense Item Name is required";
    if (!formData.amount) newErrors.amount = "Amount is required";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) toast.error("Please fill in required fields.");
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const handleSelectChange = (option) => {
    setFormData((prev) => ({ ...prev, name: option }));
    if (errors.name) setErrors(prev => { const n = { ...prev }; delete n.name; return n; });
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const dataToSave = {
      name: formData.name.value,
      amount: Number(formData.amount),
      note: formData.note,
      id: isEditMode ? expenseToEdit.id : null,
    };
    try {
      setIsLoading(true);
      await onSave(dataToSave, isEditMode);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const customStyles = {
    control: (provided, state) => ({ ...provided, borderColor: state.isFocused ? '#3b82f6' : errors.name ? '#ef4444' : '#d1d5db', fontSize: '14px' }),
    option: (provided, state) => ({ ...provided, fontSize: '14px', backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#ebf8ff' : 'white', color: state.isSelected ? 'white' : 'black' }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg overflow-visible">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-700">{isEditMode ? "Edit Expense Bill" : "Add Expense Bill"}</h2>
        </div>
        <div className="p-6 grid gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expense Item Name *</label>
            <CreatableSelect
              options={itemOptions}
              value={formData.name}
              onChange={handleSelectChange}
              onCreateOption={handleCreateItem}
              placeholder="Select or type to add..."
              isClearable
              isDisabled={isLoading}
              formatCreateLabel={(v) => `Add "${v}" as new item`}
              styles={customStyles}
            />
            {errors.name && <span className="text-red-500 text-xs">{errors.name}</span>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
            <input type="number" name="amount" className={`w-full p-2 border rounded text-sm ${errors.amount ? 'border-red-500' : 'border-gray-300'}`} value={formData.amount} onChange={handleChange} placeholder="Enter amount" disabled={isLoading} />
            {errors.amount && <span className="text-red-500 text-xs">{errors.amount}</span>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
            <textarea name="note" rows="3" className="w-full p-2 border rounded text-sm border-gray-300" value={formData.note} onChange={handleChange} placeholder="Enter details..." disabled={isLoading} />
          </div>
        </div>
        <div className="p-6 border-t flex justify-end gap-3 bg-gray-50 rounded-b-lg">
          <button onClick={onClose} className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300" disabled={isLoading}>Cancel</button>
          <button onClick={handleSave} className="bg-M-primary-color/90 hover:bg-M-primary-color text-sm text-white py-2 px-6 rounded-md transition-all duration-200 flex items-center gap-2" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Bill"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NicuOfficeExpenseModal;